import { motion } from "framer-motion";
import { Settings, Sun, Moon, Monitor, LogOut, User, Palette, RefreshCw } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../components/ProtectedRoute";
import { cn } from "../lib/utils";
import { toast } from "sonner";

export default function SettingsPage() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t, isRTL } = useLanguage();
  const { user, logout } = useAuth();

  const handleForceUpdate = async () => {
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Unregister service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      
      toast.success(isRTL ? "جاري التحديث..." : "Updating...");
      
      // Reload the page
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(isRTL ? "فشل التحديث" : "Update failed");
    }
  };

  const themeOptions = [
    { value: "light", icon: Sun, label: t("light") },
    { value: "dark", icon: Moon, label: t("dark") },
    { value: "auto", icon: Monitor, label: t("auto") }
  ];

  return (
    <div className="min-h-screen p-6 pt-20 md:pt-6 pb-24 md:pb-6 bg-background" data-testid="settings-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
              <Settings className="w-7 h-7" />
            </div>
            <div>
              <h1 className="heading-md text-foreground">{t("settingsTitle")}</h1>
              <p className="text-muted-foreground text-sm">{t("customizeExperience")}</p>
            </div>
          </div>
        </motion.header>

        <div className="space-y-6">
          {/* User Profile */}
          {user && (
            <motion.section 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-elevated p-6"
            >
              <h2 className="font-semibold mb-4 flex items-center gap-2 text-foreground">
                <User className="w-5 h-5 text-muted-foreground" />
                {t("account")}
              </h2>
              
              <div className="flex items-center gap-4 mb-6">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-16 h-16 rounded-2xl border-2 border-border object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                    <User className="w-8 h-8" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-lg truncate">{user.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>

              <motion.button
                onClick={logout}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive hover:bg-destructive/15 transition-all font-medium"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span>{t("logout")}</span>
              </motion.button>
            </motion.section>
          )}

          {/* Theme Settings */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-elevated p-6"
          >
            <h2 className="font-semibold mb-2 flex items-center gap-2 text-foreground">
              <Palette className="w-5 h-5 text-muted-foreground" />
              {t("appearance")}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">{t("chooseTheme")}</p>

            <div className="grid grid-cols-3 gap-4">
              {themeOptions.map((option) => (
                <motion.button
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`theme-${option.value}-btn`}
                  className={cn(
                    "relative flex flex-col items-center gap-3 p-5 rounded-2xl transition-all duration-200 border-2",
                    theme === option.value 
                      ? "bg-primary text-primary-foreground border-primary shadow-lg" 
                      : "bg-secondary text-muted-foreground border-transparent hover:text-foreground hover:border-border"
                  )}
                >
                  <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    theme === option.value ? "bg-primary-foreground/20" : "bg-background"
                  )}>
                    <option.icon className="w-6 h-6" />
                  </div>
                  <span className="font-medium">{option.label}</span>
                  
                  {/* Active Indicator */}
                  {theme === option.value && (
                    <motion.div 
                      layoutId="activeTheme"
                      className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-primary-foreground"
                    />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Current Theme Display */}
            <div className="mt-6 px-4 py-3 rounded-xl bg-secondary border border-border text-center">
              <span className="text-sm text-muted-foreground">
                {t("currentTheme")}: <span className="text-foreground font-semibold">{resolvedTheme === 'dark' ? t("dark") : t("light")}</span>
              </span>
            </div>
          </motion.section>

          {/* App Update Section */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-elevated p-6"
          >
            <h2 className="font-semibold mb-2 flex items-center gap-2 text-foreground">
              <RefreshCw className="w-5 h-5 text-muted-foreground" />
              {isRTL ? "تحديث التطبيق" : "App Update"}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              {isRTL ? "امسح الكاش وحمّل أحدث إصدار" : "Clear cache and load latest version"}
            </p>

            <motion.button
              onClick={handleForceUpdate}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all font-medium"
              data-testid="force-update-btn"
            >
              <RefreshCw className="w-5 h-5" />
              <span>{isRTL ? "تحديث الآن" : "Update Now"}</span>
            </motion.button>
          </motion.section>
        </div>
      </div>
    </div>
  );
}
